import React, { useState, useRef } from 'react';
import { Form, Input, Button, Card, message, Typography, Space } from 'antd';
import { UserOutlined, LockOutlined, LoginOutlined, VideoCameraOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import GalaxyBackground from '../components/GalaxyBackground';
import SliderCaptcha from '../components/SliderCaptcha';

const { Title, Text } = Typography;

interface LoginForm {
  username: string;
  password: string;
}

const LoginPage: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [captchaVisible, setCaptchaVisible] = useState(false);
  const pendingValuesRef = useRef<LoginForm | null>(null);
  const navigate = useNavigate();
  const { login } = useAuth();

  // 表单提交 -> 先弹出滑块验证
  const handleFormSubmit = async (values: LoginForm) => {
    pendingValuesRef.current = values;
    setCaptchaVisible(true);
  };

  // 滑块验证通过 -> 执行实际登录
  const handleCaptchaSuccess = async () => {
    setCaptchaVisible(false);
    const values = pendingValuesRef.current;
    if (!values) return;

    setLoading(true);
    console.log('开始登录，用户名:', values.username);
    
    try {
      console.log('调用登录API...');
      const result = await login(values.username, values.password);
      console.log('登录API响应:', result);
      
      if (result.success) {
        console.log('登录成功，准备跳转到主页');
        message.success(result.message || '登录成功！');
        navigate('/'); 
      } else {
        console.log('登录失败:', result.message);
        message.error(result.message || '登录失败，请检查用户名和密码');
      }
    } catch (error) {
      console.error('登录过程中发生错误:', error);
      console.log(error);
      message.error('网络错误，请稍后重试');
    } finally {
      setLoading(false);
      pendingValuesRef.current = null;
      console.log('登录流程结束');
    }
  };

  return (
    <div style={{ 
      minHeight: '100vh', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      position: 'relative',
      overflow: 'hidden'
    }}>
      <GalaxyBackground />
      
      <Card 
        style={{ 
          width: 420, 
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3), 0 0 80px rgba(100, 150, 255, 0.2)',
          borderRadius: '16px',
          border: '1px solid rgba(255, 255, 255, 0.3)',
          position: 'relative',
          zIndex: 1
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <Space direction="vertical" align="center" size="middle">
            <div style={{ 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: '50%',
              padding: '20px',
              boxShadow: '0 4px 20px rgba(102, 126, 234, 0.4)'
            }}>
              <VideoCameraOutlined style={{ fontSize: 48, color: '#fff' }} />
            </div>
            <Title level={2} style={{ 
              margin: 0,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontWeight: 'bold'
            }}>
              集木视频生成系统
            </Title>
            <Text style={{ color: '#666', fontSize: '14px' }}>
              探索无限创意，开启视频新纪元
            </Text>
          </Space>
        </div>

        <Form
          form={form}
          name="login"
          onFinish={handleFormSubmit}
          autoComplete="off"
          size="large"
        >
          <Form.Item
            name="username"
            rules={[
              { required: true, message: '请输入用户名!' },
              { min: 3, message: '用户名至少3个字符!' }
            ]}
          >
            <Input
              prefix={<UserOutlined />}
              placeholder="用户名"
              autoComplete="username"
            />
          </Form.Item>

          <Form.Item
            name="password"
            rules={[
              { required: true, message: '请输入密码!' },
              { min: 6, message: '密码至少6个字符!' }
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="密码"
              autoComplete="current-password"
            />
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              block
              style={{
                height: '45px',
                fontSize: '16px',
                fontWeight: 'bold',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                border: 'none',
                boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.6)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 15px rgba(102, 126, 234, 0.4)';
              }}
            >
              {loading ? '登录中...' : '登录'}
            </Button>
          </Form.Item>
        </Form>

        <div style={{ 
          textAlign: 'center', 
          marginTop: 20,
          paddingTop: 20,
          borderTop: '1px solid rgba(0, 0, 0, 0.06)'
        }}>
          <Text style={{ 
            color: '#999',
            fontSize: '12px'
          }}>
            © 2025 集木科技 · 让创意触手可及
          </Text>
        </div>
      </Card>

      <SliderCaptcha
        visible={captchaVisible}
        onSuccess={handleCaptchaSuccess}
        onClose={() => setCaptchaVisible(false)}
      />
    </div>
  );
};

export default LoginPage;